import {
  cancelDownload,
  writeDownloadedChunksToDisk,
  updateTimeStatusOfDownloadingFileCommon,
  handleDownloadRetry,
  resumeDownload,
  pausedDownload,
  pauseDownload,
  chunkNosFromList,
  findRemainingChunks,
} from "/WebInterface/new-ui/modules/advanced-download/common.js";
import downloadingQueue from "/WebInterface/new-ui/modules/advanced-download/downloading-queue.js";
import store from "/WebInterface/new-ui/store/index.js";
import C from "/WebInterface/new-ui/assets/js/utils/log.js";
import { _$ } from "/WebInterface/new-ui/assets/js/utils/dom.js";
import { getAltDomainToken } from "/WebInterface/new-ui/assets/js/utils/api/apis.js";
import {
  isFileOpen,
  closeFile,
} from "/WebInterface/new-ui/upload/utils/apis.js";
import { ajax } from "/WebInterface/new-ui/assets/js/utils/ajax.js";

/**
 * File Downloading Activities Module
 * @constructor
 * @param {array} queue - Maintains the queue of files to download
 * @param {integer} maxRequestPerDomain - Max API requests we can hit simultaneously while downloading file
 * @param {object} streamingFile - Current downloading file
 * @param {integer} chunkWrittenToTheDisk - Number of the chunk written in the local disk
 * @param {integer} bytesDownloaded - Total bytes downloaded
 * @param {integer} bytesWritten - Total bytes written in the local disk
 * @param {object} chunksRemainsToWrite - Maintains the unordered downloaded chunks that are waiting to write in the local disk
 * @param {array} chunks - Maintains chunks of the current downloading file
 * @param {object} xhrs - Maintains on going xhrs of the current downloading file
 * @param {integer} elapsedTime - Elapsed time of currently downloading file in seconds
 * @param {integer} lastBytesDownloaded - Number of bytes downloaded in the last second (For speed calculation).
 * @param {boolean} isPause - Keep status of pause of the current downloading file.
 * @param {object} failedChunks - Maintains failed chunks of current downloading file.
 * @param {integer} maxRetries - Max retries allows to the current downloading file before transferring to failed status.
 * @param {boolean} fileDownloadingInProgress - Keep status of downloading process.
 * @param {array} altDomains - alternate domains to make more parallel http request for download.
 */

class XHRAdvanceDownload {
  constructor({ altDomains }) {
    // this.queue = [];
    this.streamingFile = null;
    this.chunkWrittenToTheDisk = 0;
    this.bytesDownloaded = 0;
    this.bytesWritten = 0;
    this.bytesReceivedForSpeed = 0;
    this.lastBytesReceivedForSpeed = 0;
    this.chunksRemainsToWrite = {};
    this.chunks = [];
    this.xhrs = {};
    this.elapsedTime = 0;
    this.lastBytesDownloaded = 0;
    this.lastBytesDownloadedInTenSec = 0;
    this.isPause = false;
    this.failedChunks = {};
    this.maxRetries = 2;
    this.chunksBeingWritten = {};
    this.chunksWritten = {};
    this.EOFChunkIndex = null;
    this.fileDownloaded = false;
    this.chunksDownloaded = {};
    this.diskWriteErrorHandled = false;
    // this.fileDownloadingInProgress = false;
    this.maxRequestPerDomain = 8;
    this.maxRequest = 8;

    const getBaseUrl = (url) => {
      const urlObject = new URL(url);
      const pathSegments = urlObject.pathname
        .split("/")
        .filter((segment) => segment !== "");

      let basePath = "/";
      if (pathSegments.length > 0 && pathSegments[0] !== "WebInterface") {
        basePath += pathSegments[0];
      }

      const curDomainUrl = `${urlObject.origin}${basePath}`;
      return curDomainUrl.replace(/\/+$/, "");
    };

    const localDomain = getBaseUrl(window.location);

    this.altDomains = [localDomain, ...altDomains];

    if (altDomains.length > 0) {
      const addRequest = this.maxRequestPerDomain * altDomains.length;
      this.maxRequest += addRequest;

      const handleAltDomainToken = async () => {
        try {
          const resp = await getAltDomainToken();
          this.altDomainToken = resp;
        } catch (error) {}
      };

      const handleAltDomainTokenCall = handleAltDomainToken.bind(this);
      handleAltDomainTokenCall();
    }
  }

  // // Add new file to downloading queue :- this.queue
  // addFilesToDownloadingQueue(file) {
  //   addFilesToQueue(this, file);
  // }

  // // Function to continue the downloading queue, like after upload completes
  // continueDownload() {
  //   continueFileDownload(this);
  // }

  // Check next file in the queue and will start downloading the next file
  nextFileInQueue = (canceled) => {
    downloadingQueue.nextFileInDownloadingQueue(canceled);
  };

  // Creates set of chunks (4)
  createChunks = () => {
    const chunks = [];

    // const from = 0;
    const from = this.chunks.length;
    const to = from + this.maxRequestPerDomain;

    let currentAltDomainIndex = 0;

    for (let index = from; index < to; index++) {
      const serverName = this.altDomains[currentAltDomainIndex];

      const chunk = {
        index,
        status: "init",
        serverName,
        isAlternateDomain: currentAltDomainIndex !== 0,
        progressLoaded: 0,
      };

      currentAltDomainIndex++;

      if (currentAltDomainIndex === this.altDomains.length) {
        currentAltDomainIndex = 0;
      }

      chunks.push(chunk);
    }

    this.chunks = [...this.chunks, ...chunks];
    return chunks;
  };

  // Create next chunk to download
  nextChunk = (prevChunk) => {
    const { serverName, isAlternateDomain } = prevChunk;

    const chunk = {
      // index: 0,
      index: this.chunks.length,
      status: "init",
      serverName,
      isAlternateDomain,
      progressLoaded: 0,
    };

    this.chunks.push(chunk);
    this.downloadChunk(chunk);
  };

  checkIfFileDownloaded = () => {
    const { id: fileId } = this.streamingFile;
    const allChunks = chunkNosFromList(this.chunksDownloaded);
    const writtenChunks = chunkNosFromList(this.chunksWritten);
    const missingChunks = findRemainingChunks(allChunks);

    C.log(
      `Download: All Chunks length: ${allChunks.length}, Written Chunks length: ${writtenChunks.length}, Missing Chunks length: ${missingChunks.length}, EOFChunkIndex: ${this.EOFChunkIndex}`
    );

    if (this.EOFChunkIndex !== null && missingChunks.length > 0) {
      C.warn(
        `Download: Received last chunk, but missing chunks: ${missingChunks.join(
          ", "
        )}. So waiting for missing chunks. Chunks length ${
          allChunks.length
        }, written chunks: ${writtenChunks.length}, missing chunks: ${
          missingChunks.length
        }`
      );
    }
    this.fileDownloaded =
      this.EOFChunkIndex !== null &&
      missingChunks.length === 0 &&
      allChunks.length - 1 === writtenChunks.length;

    if (this.fileDownloaded) {
      C.success(
        `Download: Completed, writing to disk may take time, once all chunks are written it will start finalization`
      );
      if (this.compressingTimeoutId) return;
      this.compressingTimeoutId = setTimeout(() => {
        store.dispatch("updateFileStatusDownload", {
          fileId,
          updates: {
            status: "compressing",
          },
        });
      }, 3000);
      this.fileClosed = true;
      this.abortAllRunningXhr();
    }
  };

  // Take chunk as parameter and downloads it
  downloadChunk = async (chunk) => {
    const { size: fileSize, id: fileId } = this.streamingFile;
    const { index, serverName, isAlternateDomain } = chunk;
    chunk.progressLoaded = 0;
    //

    let xhr = new XMLHttpRequest();

    // Handle error of xhr
    const xhrErrorEvent = async (evt) => {
      this.failedChunks[index] = chunk;
      const { retries } = chunk;

      // Retries failed chunks
      const retryDownload = async () => {
        try {
          // Starts time counter
          this.updateTimeStatusOfDownloadingFile();

          // Downloading failed chunks
          Object.values(this.failedChunks).forEach((chunk) => {
            this.downloadChunk({ ...chunk, retries: retries + 1 || 1 });
          });

          // Reset failed chunks
          this.failedChunks = {};
        } catch (error) {}
      };

      this.handleRetry(retries || 0, retryDownload);
      return;
    };

    const errorEvent = xhrErrorEvent.bind(this);

    // Handles xhr load event
    xhr.addEventListener("load", (evt) => {
      const { response, status } = xhr;
      const contentLength = xhr.getResponseHeader("content-length");

      // Calls error event
      if (status !== 200) {
        xhrErrorEvent();
        return;
      }

      delete this.xhrs[index];

      // Ensure progress counter accounts for any missing bytes (e.g. no progress events)
      const chunkSize = parseInt(contentLength);
      const alreadyCounted = chunk.progressLoaded || 0;
      if (chunkSize > alreadyCounted) {
        this.bytesReceivedForSpeed += chunkSize - alreadyCounted;
        chunk.progressLoaded = chunkSize;
      }

      // Updates bytes downloaded status of file
      const bytesDownloaded = (this.bytesDownloaded += chunkSize);

      this.chunksRemainsToWrite[index] = {
        data: new Uint8Array(response),
        size: chunkSize,
      };

      store.dispatch("updateProgressBarDownload", {
        fileId,
        bytesDownloaded,
        fileSize,
      });

      // Controls downloading flow
      const { isPause, chunkWrittenToTheDisk, xhrs } = this;

      const canContinueWritingChunks = index === chunkWrittenToTheDisk;
      const noContentLeft = parseInt(contentLength) === 0;

      if (this.chunksDownloaded[index] === undefined) {
        this.chunksDownloaded[index] = index;
      }

      if (canContinueWritingChunks) this.writeChunksInDisk();
      if (isPause) {
        if (Object.values(xhrs).length === 0) {
          this.paused();
        }
        return;
      }
      if (noContentLeft) {
        this.EOFChunkIndex = index;
        C.log(`Download: Received last chunk, 0 size chunk ${index}, ignored`);
        return;
      }

      this.nextChunk(chunk);
    });

    xhr.addEventListener("progress", (evt) => {
      if (this.fileClosed) {
        return;
      }
      const loaded = evt.loaded || 0;
      const lastLoaded = chunk.progressLoaded || 0;
      const delta = loaded - lastLoaded;
      if (delta > 0) {
        this.bytesReceivedForSpeed += delta;
        chunk.progressLoaded = loaded;
      }
    });

    // Handle abort event
    xhr.addEventListener("abort", (evt) => {
      console.log("aborted");
      delete this.xhrs[index];
    });

    // Handles error event
    xhr.addEventListener("error", errorEvent);

    let url = `${serverName}/D/${this.transferId}~`;

    if (isAlternateDomain) {
      url += `${this.altDomainToken}`;
    } else {
      url += `${index + 1}`;
    }

    xhr.open("GET", url, true);

    if (isAlternateDomain)
      xhr.setRequestHeader("X-TRANSFERSEGMENT", `${index + 1}`);

    xhr.responseType = "arraybuffer";
    xhr.send();
    this.xhrs[index] = xhr;
  };

  // Calculate speed and time remaining to download the file
  updateTimeStatusOfDownloadingFile = () => {
    updateTimeStatusOfDownloadingFileCommon(this);
  };

  // Initiate downloading of the file
  startFileDownloading = async (file) => {
    const initDownload = async () => {
      // Reset all required variables
      this.streamingFile = file;
      this.chunkWrittenToTheDisk = 0;
      this.bytesDownloaded = 0;
      this.bytesWritten = 0;
      this.bytesReceivedForSpeed = 0;
      this.lastBytesReceivedForSpeed = 0;
      this.chunksRemainsToWrite = {};
      this.chunks = [];
      this.xhrs = {};
      this.chunksBeingWritten = {};
      this.chunksWritten = {};
      this.EOFChunkIndex = null;
      this.fileDownloaded = false;
      this.chunksDownloaded = {};
      this.elapsedTime = 0;
      this.lastBytesDownloaded = 0;
      this.lastBytesDownloadedInTenSec = 0;
      this.isPause = false;
      this.retrying = false;
      this.compressingTimeoutId = null;
      this.diskWriteErrorHandled = false;

      const { id: fileId } = file;

      const c2f = utils.cookie.get("currentAuth");

      // Provides unique id to download the file
      try {
        const resp = await ajax.call({
          timeout: 10000000,
          data: {
            command: `download`,
            transfer_type: "download",
            path: file.href_path,
            c2f,
          },
        });

        if (resp) {
          const xml = new DOMParser().parseFromString(resp, "text/xml");
          const result = utils.xmlToJson(xml);

          this.transferId = result?.commandResult?.response;

          if (this.fileClosed) {
            return;
          }

          this.writableStream = await file.fileHandle.createWritable();

          store.dispatch("updateFileStatusDownload", {
            fileId,
            updates: {
              status: "downloading",
            },
          });

          // Start downloading chunks
          this.startDownloadingNextBunchOfChunks();
        }
      } catch (error) {
      } finally {
        if (this.fileClosed) {
          await closeFile({
            transfer_type: "download",
            download_id: this.transferId,
            random: Math.random(),
          });

          const { id: fileId } = file;
          store.dispatch("updateFileStatusDownload", {
            fileId,
            updates: {
              status: "canceled",
              downloadingError: "canceled",
            },
          });

          this.nextFileInQueue(true);
        }
      }
    };

    const checkFileAndStartDownload = async () => {
      try {
        const fileStatus = await isFileOpen({
          transfer_type: "download",
          random: Math.random(),
          path: file.href_path,
        });

        if (this.fileClosed) {
          return;
        }

        let hasFile = false;
        Object.keys(store.downloadFilesDetails.files).forEach((key) => {
          const file = store.downloadFilesDetails.files[key];
          if (file.href_path === file.href_path) {
            hasFile = file;
          }
        });
        if (!hasFile) {
          console.log("File removed.");
          return;
        }
        if (
          fileStatus !== "CLOSED" ||
          fileStatus.toLowerCase().indexOf("error") !== -1 ||
          fileStatus.toLowerCase().indexOf("failure") !== -1
        ) {
          setTimeout(() => {
            checkFileAndStartDownload();
          }, 5000);
          return;
        }

        initDownload();
      } catch (error) {
        if (this.fileClosed) {
          return;
        }

        console.log(error);
        setTimeout(() => {
          checkFileAndStartDownload();
        }, 5000);
      } finally {
        if (this.fileClosed) {
          const { id: fileId } = file;
          store.dispatch("updateFileStatusDownload", {
            fileId,
            updates: {
              status: "canceled",
              downloadingError: "canceled",
            },
          });

          this.nextFileInQueue(true);
        }
      }
    };

    checkFileAndStartDownload();
  };

  closeFileDownloading = async () => {
    const file = this.streamingFile;
    const { id: fileId } = file;

    try {
      // Close writable stream
      const response = await closeFile({
        transfer_type: "download",
        download_id: this.transferId,
        random: Math.random(),
      });

      await this.writableStream.close();

      clearTimeout(this.compressingTimeoutId);

      clearInterval(this.elapsedIntervalId);
      clearInterval(this.intervalIdAverageTenSecondSpeed);
      // Set 100% progress when download is truly complete
      store.dispatch("updateFileStatusDownload", {
        fileId,
        updates: {
          status: "downloaded",
          timeTakenToComplete: this.elapsedTime ? this.elapsedTime * 1000 : 200,
          bytesDownloaded: this.streamingFile.size, // Show actual file size (100%)
          fileSize: this.streamingFile.size,
        },
      });
      this.nextFileInQueue();
    } catch (error) {
      clearInterval(this.elapsedIntervalId);
      clearInterval(this.intervalIdAverageTenSecondSpeed);
      console.log(error);
    }
  };

  // Starts downloading next set of chunks after any pause or discontinue
  startDownloadingNextBunchOfChunks = () => {
    const chunks = this.createChunks();
    chunks.forEach((chunk) => {
      this.downloadChunk(chunk);
    });

    this.updateTimeStatusOfDownloadingFile();
    downloadingQueue.handleUpdateDownloadingStatus(true);
  };

  handleDiskWriteError = async (message, error) => {
    if (this.diskWriteErrorHandled) {
      return;
    }
    this.diskWriteErrorHandled = true;
    this.fileClosed = true;
    clearInterval(this.elapsedIntervalId);
    clearInterval(this.intervalIdAverageTenSecondSpeed);
    this.abortAllRunningXhr();
    try {
      await closeFile({
        transfer_type: "download",
        download_id: this.transferId,
        random: Math.random(),
      });
    } catch (err) {
      C.warn(
        `Download: Error closing file after disk failure: ${err?.message}`
      );
    }

    try {
      if (this.writableStream) {
        await this.writableStream.close();
      }
    } catch (err) {
      C.warn(`Download: Writable stream close failed: ${err?.message}`);
    }

    const fileId = this.streamingFile?.id;
    if (fileId) {
      store.dispatch("updateFileStatusDownload", {
        fileId,
        updates: {
          status: "failed",
          downloadingError:
            "File failed please retry to download file: " +
            (message || error?.message || "Disk write failed"),
        },
      });
    }

    this.nextFileInQueue(true);
  };

  // Writes chunks in the disk using the file handle (FileSystemAPI :- FileHandle)
  writeChunksInDisk = () => {
    writeDownloadedChunksToDisk(this);
  };

  // Aborts all running xhr, uses when download completes or canceled
  abortAllRunningXhr = () => {
    Object.values(this.xhrs).forEach((xhr) => {
      xhr.abort();
    });
  };

  // pauses downloading file
  pause = () => {
    pauseDownload(this);
  };

  // Triggers after successful pause of the downloading file
  paused = () => {
    pausedDownload(this);
  };

  // Resume file downloading after pause
  resume = () => {
    const onResume = () => {
      this.startDownloadingNextBunchOfChunks();
    };
    resumeDownload(this, onResume);
  };

  // Cancel downloading file
  cancel = () => {
    if (!this.transferId) {
      this.fileClosed = true;
      return;
    }

    const beforeCancelingFile = () => {
      clearInterval(this.elapsedIntervalId);
      clearInterval(this.intervalIdAverageTenSecondSpeed);

      this.abortAllRunningXhr();
    };
    const afterCancelingFile = async () => {
      const response = await closeFile({
        transfer_type: "download",
        download_id: this.transferId,
        random: Math.random(),
      });

      this.nextFileInQueue(true);
    };

    cancelDownload({
      streamingFile: this.streamingFile,
      writableStream: this.writableStream,
      beforeCancelingFile,
      afterCancelingFile,
    });
  };

  remove = (fileId) => {
    if (!fileId) return;
    this.queue = this.queue.filter(({ id }) => id !== fileId);
    store.dispatch("deleteFileInDownload", { id: fileId });
  };

  // Update downloading status globally in the project
  handleUpdateDownloadingStatus = (downloading) => {
    if (
      (downloading === true || downloading === false) &&
      downloading !== this.fileDownloadingInProgress
    ) {
      this.fileDownloadingInProgress = downloading;

      store.dispatch("setChunkProcessingStatus", {
        type: "isDownloading",
        status: this.fileDownloadingInProgress,
      });
    }

    return this.fileDownloadingInProgress;
  };

  // Handle retry of the failed chunks
  handleRetry = (retries, cb = () => {}) => {
    const onRetryOver = () => {
      this.abortAllRunningXhr();
      this.retryChunksQueue = [];
    };
    handleDownloadRetry(this, retries, cb, onRetryOver);
  };
}

export default XHRAdvanceDownload;
